/**
 * Owner: Masa Bayu
 * File: QxStudioBar.tsx
 * Date:Time: 2026-10-29 11:55
 * Description: Bar Studio Write — back/brand, tajuk, Import PDF ke naskhah Write, Publish.
 */

import { useSyncExternalStore, type ReactElement } from 'react';
import { getQxStudioView, subscribeQxStudio } from '@qx/qxw-core';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import { importPageFraction } from '@qx/qxwriter-app/lib/qx-writer-import-fraction';
import { QxStudioImportGate } from './QxStudioImportGate';
import './qx-studio-bar.css';

export function QxStudioBar({
  onBack,
}: {
  readonly onBack: () => void;
}): ReactElement {
  const view = useSyncExternalStore(subscribeQxStudio, getQxStudioView);
  const st = view.state;
  const cmd = view.commands;
  const importing = st.importBusy === true;
  const opened = importPageFraction(st.importHint ?? '') ?? 0;
  return (
    <header
      className="qx-studio-bar"
      role="banner"
      data-qx-studio-mode="write"
      data-qx-studio-bar=""
      data-qx-studio-import={importing ? 'busy' : undefined}
    >
      <div className="qx-studio-bar__left">
        <button
          type="button"
          className="qx-studio-bar__icon"
          aria-label="Back to dashboard"
          title="Back to dashboard"
          onClick={onBack}
          data-qx-studio-back=""
        >
          <LandingIcon name="chevronLeft" size={14} />
        </button>
        <span className="qx-studio-bar__mark" aria-hidden="true">QX</span>
        <span className="qx-studio-bar__brand">Studio</span>
      </div>
      <div className="qx-studio-bar__center">
        {cmd.setTitle ? (
          <input
            type="text"
            className="qx-studio-bar__title-input"
            value={st.title ?? ''}
            placeholder="Untitled"
            aria-label="Document title"
            onChange={(e) => cmd.setTitle?.(e.target.value)}
            data-qx-studio-title-input=""
          />
        ) : (
          <span
            className={`qx-studio-bar__title${st.title ? '' : ' qx-studio-bar__title--empty'}`}
            title={st.title || undefined}
            data-qx-studio-title=""
          >
            {st.title || 'Untitled'}
          </span>
        )}
        <span
          className={`qx-studio-bar__saved${st.unsaved ? ' qx-studio-bar__saved--dirty' : ''}`}
          data-qx-studio-saved={st.unsaved ? 'dirty' : 'clean'}
        >
          {st.unsaved ? '● Unsaved' : '✓ Saved'}
        </span>
        <button
          type="button"
          className="qx-studio-bar__icon"
          aria-label="Undo"
          title="Undo"
          onClick={cmd.undo}
          disabled={!cmd.undo}
          data-qx-studio-undo=""
        >
          ↶
        </button>
        <button
          type="button"
          className="qx-studio-bar__icon"
          aria-label="Redo"
          title="Redo"
          onClick={cmd.redo}
          disabled={!cmd.redo}
          data-qx-studio-redo=""
        >
          ↷
        </button>
      </div>
      <div className="qx-studio-bar__right">
        {cmd.importPdf ? (
          <button
            type="button"
            className="qx-studio-bar__action"
            onClick={cmd.importPdf}
            aria-label="Import PDF"
            disabled={importing}
            data-qx-studio-import-pdf=""
          >
            {importing ? 'Importing…' : 'Import'}
          </button>
        ) : null}
        {cmd.openPublish ? (
          <button
            type="button"
            className="qx-studio-bar__action"
            onClick={cmd.openPublish}
            disabled={importing}
            data-qx-studio-publish=""
          >
            Publish
          </button>
        ) : null}
      </div>
      {importing ? (
        <>
          <div
            className="qx-studio-bar__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(opened * 100)}
            aria-valuetext={st.importHint ?? 'Importing PDF'}
            aria-busy="true"
            data-qx-studio-import-progress=""
          >
            <span className="qx-studio-bar__progress-fill" style={{ width: `${opened * 100}%` }} />
          </div>
          <QxStudioImportGate
            hint={st.importHint ?? 'Reading PDF…'}
            failed={st.importFailed === true}
            {...(cmd.dismissImport ? { onDismiss: cmd.dismissImport } : {})}
          />
        </>
      ) : null}
    </header>
  );
}
